import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme, Text } from '@Sage/hoi-poi-ui';

import { SALESORDERS } from 'constants/Entities';
import { getLiteral } from 'utils/getLiteral';
import { formatDecimalByConfig, DECIMAL_TYPES } from 'utils/decimal';

import './styles.scss';

/** Wide layout (min-width: 1200px in styles.scss). Kept for callers that need a static default. */
export const SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT = 94;

const SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT_COMPACT = 210;
const SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT_MEDIUM = 156;

/** Max width for the compact (tallest) layout — keep in sync with styles.scss. */
export const SALES_ORDERS_BOTTOM_INFO_PANEL_MAX_WIDTH_COMPACT = 879;
/** Min width for the wide (shortest) layout — keep in sync with styles.scss. */
export const SALES_ORDERS_BOTTOM_INFO_PANEL_MIN_WIDTH_WIDE = 1200;

export function getSalesOrdersBottomInfoPanelHeight(viewportWidth) {
    if (viewportWidth <= SALES_ORDERS_BOTTOM_INFO_PANEL_MAX_WIDTH_COMPACT) {
        return SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT_COMPACT;
    }
    if (viewportWidth < SALES_ORDERS_BOTTOM_INFO_PANEL_MIN_WIDTH_WIDE) {
        return SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT_MEDIUM;
    }
    return SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT;
}

export function useSalesOrdersBottomInfoPanelHeight() {
    const [height, setHeight] = useState(() =>
        typeof window !== 'undefined'
            ? getSalesOrdersBottomInfoPanelHeight(window.innerWidth)
            : SALES_ORDERS_BOTTOM_INFO_PANEL_HEIGHT,
    );

    useEffect(() => {
        const onResize = () => {
            setHeight(getSalesOrdersBottomInfoPanelHeight(window.innerWidth));
        };
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    return height;
}

const ITEM_TYPE_FIELD = 'field';
const ITEM_TYPE_SEPARATOR = 'separator';
const ITEM_TYPE_HORIZONTAL_RULE = 'horizontalRule';

const PLACEHOLDER_VALUE = '-';

const SalesOrdersBottomInfoPanel = memo(({ data }) => {
    const theme = useTheme();

    const idEnvironment = useMemo(() => data?.idBranch?.value ?? data?.idBranch, [data?.idBranch]);
    const idCurrency = useMemo(
        () => data?.idCurrency?.value ?? data?.idCurrency,
        [data?.idCurrency],
    );

    const formatAmount = useCallback(
        (value, fieldId) => {
            return value !== null
                ? formatDecimalByConfig({
                      value,
                      type: DECIMAL_TYPES.CURRENCY_GENERAL,
                      idEnvironment,
                      idCurrency,
                      entityName: SALESORDERS.extraFieldName,
                      fieldId,
                  })
                : PLACEHOLDER_VALUE;
        },
        [idEnvironment, idCurrency],
    );

    const items = useMemo(() => {
        return [
            {
                type: ITEM_TYPE_FIELD,
                label: getLiteral('label_salesorder_header_subtotal'),
                value: formatAmount(data?.subtotalAmount ?? 0, 'subtotalAmount'),
            },
            {
                type: ITEM_TYPE_FIELD,
                label: getLiteral('label_salesorder_header_total_discounts'),
                value: formatAmount(data?.discountTotalAmount ?? 0, 'discountTotalAmount'),
            },
            {
                type: ITEM_TYPE_FIELD,
                label: getLiteral('label_salesorder_header_tax_base'),
                value: formatAmount(data?.taxableBaseAmount ?? 0, 'taxableBaseAmount'),
            },
            { type: ITEM_TYPE_HORIZONTAL_RULE },
            {
                type: ITEM_TYPE_FIELD,
                label: getLiteral('label_salesorder_header_total_vat'),
                value: formatAmount(data?.taxTotalAmount ?? 0, 'taxTotalAmount'),
            },
            { type: ITEM_TYPE_SEPARATOR },
            {
                type: ITEM_TYPE_FIELD,
                label: getLiteral('label_salesorder_header_total_including_taxes'),
                value: formatAmount(data?.finalTotalAmount ?? 0, 'finalTotalAmount'),
                isTotal: true,
            },
        ];
    }, [
        formatAmount,
        data?.subtotalAmount,
        data?.discountTotalAmount,
        data?.taxableBaseAmount,
        data?.taxTotalAmount,
        data?.finalTotalAmount,
    ]);

    return (
        <div className="sales-orders-bottom-info-panel">
            {items.map((item) => {
                switch (item.type) {
                    case ITEM_TYPE_HORIZONTAL_RULE:
                        return (
                            <div
                                key="horizontalRule"
                                className="sales-orders-bottom-info-panel__horizontal-rule"
                            />
                        );
                    case ITEM_TYPE_SEPARATOR:
                        return (
                            <div
                                key="separator"
                                className="sales-orders-bottom-info-panel__separator"
                            />
                        );
                    default:
                        return (
                            <div key={item.label} className="sales-orders-bottom-info-panel__item">
                                <Text
                                    type="caption"
                                    color={theme.colors.utility.textSecondary}
                                    className="sales-orders-bottom-info-panel__label"
                                    isTruncated
                                >
                                    {item.label}
                                </Text>
                                <Text
                                    type={item.isTotal ? 'subtitle1' : 'body'}
                                    medium
                                    className="sales-orders-bottom-info-panel__value"
                                >
                                    {item.value}
                                </Text>
                            </div>
                        );
                }
            })}
        </div>
    );
});

export default SalesOrdersBottomInfoPanel;
